import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import i18n from '@common/i18n.js';
import {ENROLMENT_TYPE} from '@common/consts/survey';
import formatExcelTimeString from "@common/utils/formatExcelTimeString";

const FINGER_PRINT = 'vtm#%b4hX2Vy-20220614-1';

export async function validateExcelProgram({fileBase64, filters, enrolmentType, isAllowDuplicateApply, targetOrigin}) {
    try {
        fileBase64 = fileBase64.split(',')[1];

        const workbook = await XlsxPopulate.fromDataAsync(fileBase64, {base64: true});
        const fingerPrintSheet = workbook.sheet(1);

        if (!fingerPrintSheet || fingerPrintSheet.cell('A1').value() !== FINGER_PRINT) {
            window.parent.postMessage(
                {
                    type: 'NEXT_ON_FILE_VALIDATE_PENDING',
                    data: {
                        isValid: false,
                        messages: [i18n.t('excelProgramService_notMatchExcel')],
                    }
                },
                targetOrigin,
            )
            return;
        }

        const sheet = workbook.sheet(0);
        const programs = [];
        const gridData = [];
        const usedRangeEndCell = sheet.usedRange().endCell();
        const lastRowNumber = usedRangeEndCell.rowNumber();
        const lastColumnNumber = usedRangeEndCell.columnNumber();

        const costNameColumn = {};
        for (let i = 15; i <= lastColumnNumber; i++) {
            const costName = cellValueToStringAndTrim({cell: sheet.cell(6, i)});
            if (costName) {
                costNameColumn[i] = costName;
            }
        }

        for (let i = 7; i <= lastRowNumber; i++) {
            const row = sheet.row(i);
            const rowData = {};

            let columnNumber = 2;
            rowData.programName = cellValueToStringAndTrim({cell: row.cell(columnNumber++)});

            for (let j = 1; j <= 7; j++) {
                rowData[`day${j}`] = cellValueToStringAndTrim({cell: row.cell(columnNumber++)});
            }

            rowData.elementary = cellValueToStringAndTrim({cell: row.cell(columnNumber++)}) || '';
            rowData.middle = cellValueToStringAndTrim({cell: row.cell(columnNumber++)}) || '';
            rowData.high = cellValueToStringAndTrim({cell: row.cell(columnNumber++)}) || '';

            rowData.limit = row.cell(columnNumber++).value();
            rowData.reservedLimit = row.cell(columnNumber++).value();

            for (let j = 15; j <= lastColumnNumber; j++) {
                rowData[costNameColumn[j]] = row.cell(j).value();
            }
            if (Object.values(rowData).every(value => !value)) {
                continue;
            }

            rowData._validation = {};

            if (!rowData.programName) {
                rowData._validation.programName = {message: i18n.t('excelProgramService_noProgramName')};
            }

            if (!isAllowDuplicateApply) {

                let foundTime = false;
                for (let j = 1; j <= 7; j++) {
                    const key = `day${j}`;
                    const time = rowData[key];

                    if (!time) {
                        continue;
                    }

                    if (!time.match(/^\d{2}:\d{2}~\d{2}:\d{2}$/)) {
                        rowData._validation[key] = {message: i18n.t('excelProgramService_invalidTimeFormat')};
                    }

                    foundTime = true;
                }

                if (!foundTime) {
                    rowData._validation.day1 = {message: i18n.t('excelProgramService_noTime')};
                }

            }
            const maxGrade = {
                // 초등 칸을 유치원도 함께 사용하고 있어서, 유치원용 값 범위는 찾기 위하여 이렇게 전달된 필터에서 가장 큰 값을 찾는다.
                elementary: filters.map(f => f.shortLabel).reduce((max, current) => current > max ? current : max, 0),
                // 실제 초등 학년 수
                elementaryActual: 6,
                middle: 3,
                high: 3,
            };

            ['elementary', 'middle', 'high'].forEach(level => {
                if (
                    rowData[level].split(',')
                        .map(grade => parseInt(grade, 10))
                        .some(grade => Number.isNaN(grade) || grade < 1 || grade > maxGrade[level])
                ) {
                    rowData._validation[level] = {message: i18n.t('excelProgramService_invalidGradeFormat')};
                }
            });

            if ((enrolmentType === ENROLMENT_TYPE.FCFS || enrolmentType === ENROLMENT_TYPE.DRAW_PRIOR) && !rowData.limit) {
                rowData._validation.limit = {message: i18n.t('excelProgramService_noLimit')};
            }

            gridData.push(rowData);

            if (
                Object.keys(rowData._validation)
                    // 대상학교 벨리데이션은 티처 웹에서 학교급에 따라 사용하기 때문에 제외한다.
                    .filter(key => !['elementary', 'middle', 'high'].includes(key))
                    .length
            ) {
                continue;
            }

            programs.push({
                programName: rowData.programName,
                filters: [
                    ...(!rowData._validation.elementary && rowData.elementary.split(',').map(grade => `G${parseInt(grade, 10)}`) || []),
                    ...(!rowData._validation.middle && rowData.middle.split(',').map(grade => `G${parseInt(grade, 10) + maxGrade.elementaryActual}`) || []),
                    ...(!rowData._validation.high && rowData.high.split(',')
                                                            .map(grade => `G${parseInt(grade, 10) + maxGrade.elementaryActual + maxGrade.middle}`) || []),
                ],
                limit: rowData.limit,
                reservedLimit: rowData.reservedLimit,
                times: [1, 2, 3, 4, 5, 6, 7]
                    .map(day => ({day, time: rowData[`day${day}`]}))
                    .filter(({time}) => time)
                    .map(({day, time}) => {
                        const m = /^(\d{2}):(\d{2})~(\d{2}):(\d{2})$/.exec(time);
                        if (typeof day === 'number' && typeof time === 'string' && m === null) {
                            throw new Error(`시간은 ${formatExcelTimeString(time)}의 형태로 입력해주세요.`);
                        }
                        return {
                            day: day % 7,
                            startHour: m[1],
                            startMinute: m[2],
                            endHour: m[3],
                            endMinute: m[4],
                        }
                    }),
                costs: (() => {
                    const costs = Object.values(costNameColumn);

                    if (costs.length) {
                        return costs.map(name => ({
                            name,
                            value: rowData[name],
                        }));
                    }

                    return null;
                })(),
            });
        }

        if (!gridData.length) {
            window.parent.postMessage(
                {
                    type: 'NEXT_ON_FILE_VALIDATE_PENDING',
                    data: {
                        isValid: false,
                        messages: [i18n.t('excelProgramService_noProgram')],
                    }
                },
                targetOrigin,
            )
        } else {
            window.parent.postMessage(
                {
                    type: 'NEXT_ON_FILE_VALIDATE_PENDING',
                    data: {
                        isValid: true,
                        gridData,
                        costNames: Object.values(costNameColumn),
                        programs,
                    }
                },
                targetOrigin,
            )
        }
    } catch (e) {
        window.parent.postMessage(
            {
                type: 'NEXT_ON_FILE_VALIDATE_PENDING',
                data: {
                    isValid: false,
                    messages: e.message,
                }
            },
            targetOrigin,
        )
        console.error(e);
    }
}

/**
 * 강좌 일괄 추가용 엑셀 다운로드
 * @param enrolmentType
 * @param programs
 * @param filters - 학년 필터
 */
export async function downloadExcelProgram({enrolmentType, programs, filters}) {
    const data = await import(/* webpackChunkName: "resource-program.xlsx" */'@/iamteacherpc/assets/resources/program.xlsx');
    const workbook = await XlsxPopulate.fromDataAsync(data.default);
    const sheet = workbook.sheet(0);

    const costNames = [...new Set(
        programs
            .flatMap(program => program.costs.map(cost => cost.name))
            .filter(name => !!name),
    )];

    const costNameStartColumn = 15;
    const costNameColumn = {};
    for (let i = 0; i < costNames.length; i++) {
        const column = costNameStartColumn + i;
        sheet.cell(6, column).value(costNames[i]);
        costNameColumn[costNames[i]] = column;
    }

    let startRowNumber = 7;
    programs.forEach((program, index) => {
        const rowNumber = startRowNumber + index;

        sheet.cell(rowNumber, 2).value(program.programName);

        const dayColumnBase = 2;
        program.times.forEach(time => {
            let dayColumn = dayColumnBase + time.day;
            if (time.day === 0) {
                dayColumn = dayColumnBase + 7;
            }

            if (time.day !== null && time.day !== undefined && time.startHour && time.startMinute && time.endHour && time.endMinute) {
                const startTime = `${time.startHour.padStart(2, '0')}:${time.startMinute.padStart(2, '0')}`;
                const endTime = `${time.endHour.padStart(2, '0')}:${time.endMinute.padStart(2, '0')}`;
                sheet.cell(rowNumber, dayColumn).value(`${startTime}~${endTime}`);
            }
        });

        const gradeGroup = {
            elementary: [],
            middle: [],
            high: [],
        };

        program.filters.forEach(filter => {
            const grade = parseInt(filter.replace(/^G/, ''), 10);

            if (grade < 7) {
                gradeGroup.elementary.push(grade);
            } else if (grade < 10) {
                const gradeInSchool = grade - 6;
                gradeGroup.middle.push(gradeInSchool);
            } else {
                const gradeInSchool = grade - 9;
                gradeGroup.high.push(gradeInSchool);
            }
        });

        if (gradeGroup.elementary.length) {
            gradeGroup.elementary.sort();
            sheet.cell(rowNumber, 10).value(gradeGroup.elementary.join(','));
        }

        if (gradeGroup.middle.length) {
            gradeGroup.middle.sort();
            sheet.cell(rowNumber, 11).value(gradeGroup.middle.join(','));
        }

        if (gradeGroup.high.length) {
            gradeGroup.high.sort();
            sheet.cell(rowNumber, 12).value(gradeGroup.high.join(','));
        }

        if (enrolmentType === ENROLMENT_TYPE.FCFS || enrolmentType === ENROLMENT_TYPE.DRAW_PRIOR) {
            sheet.cell(rowNumber, 13).value(program.limit);
            sheet.cell(rowNumber, 14).value(program.reservedLimit);
        }

        program.costs.forEach(cost => {
            if (cost.name && cost.value) {
                sheet.cell(rowNumber, costNameColumn[cost.name]).value(cost.value);
            }
        });
    });

    const base64 = await workbook.outputAsync({type: 'base64'});
    return {
        filename: i18n.t('excelProgramService_fileName'),
        base64,
    }
}

function cellValue({cell}) {
    let value = cell.value();

    if (value instanceof XlsxPopulate.RichText) {
        value = value.text();
    }

    return value;
}

function cellValueToStringAndTrim({cell}) {
    return convertToStringAndTrim(cellValue({cell}));
}

function convertToStringAndTrim(value) {
    if (value === null || value === undefined) {
        return value;
    }

    return ('' + value).trim();
}
